import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLoaderData } from 'react-router-dom'
import styled from 'styled-components'

import { StyledActionButton } from '@/components/Buttons.tsx'
import { Checkbox } from '@/components/Checkbox.tsx'
import { Datepicker } from '@/components/Datepicker.tsx'
import { InputCaption } from '@/components/Label.tsx'
import { CardRadio, CardRadioGroup } from '@/components/Radio.tsx'
import { Stepper } from '@/components/Stepper.tsx'
import {
  FormElement,
  FormStyled,
  FormSubmit,
} from '@/components/templates/Form.tsx'
import { ScrollableContent } from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { BodyM, Header1, Subheader } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import { useAppointmentStore } from '@/store.ts'
import {
  getAllowedCategoriesVelthuis,
  isConsentRequiredVelthuis,
} from '@/utils/getAllowedTreatmentsVelthuis.ts'
import { VelthuisFormStep } from '@/utils/VelthuisFormSteps.ts'

import { LoaderData } from './actions.ts'

const StyledFormElement = styled(FormElement)`
  margin-bottom: 24px;
`

export const SelectCategory = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'selectCategory',
  })
  const { t: tGeneral } = useTranslation('translation', {
    keyPrefix: 'fields',
  })

  const { categories, loadTime } = useLoaderData() as LoaderData
  const {
    patientData: { dateOfBirth },
    setAppointmentType,
    setPatientData,
  } = useAppointmentStore((state) => ({
    patientData: state.patientData || {},
    setAppointmentType: state.setAppointmentType,
    setPatientData: state.setPatientData,
  }))

  const { trackFormError, trackFormInteraction } = useTrackFormProgress(
    VelthuisFormStep.SelectCategory,
    { loadTime },
  )

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [displayedCategories, setDisplayedCategories] = useState(categories)
  const [requiredConsent, setRequiredConsent] = useState<boolean>(false)
  const [isConsentChecked, setIsConsentChecked] = useState<boolean>(false)
  const [isValidationError, setIsValidationError] = useState<boolean>(false)

  useEffect(() => {
    if (dateOfBirth) {
      const age = dayjs().diff(dayjs(dateOfBirth), 'years')
      if (age < 0 || age > 120) {
        setDisplayedCategories(undefined)
        trackFormError({
          error: t('invalidDateOfBirth'),
        })
        setIsValidationError(true)
      } else {
        const allowedCategories = getAllowedCategoriesVelthuis(dateOfBirth)
        const filteredCategories = allowedCategories
          ? categories?.filter(
              (category) =>
                allowedCategories === 'all' ||
                allowedCategories?.includes(category.code),
            )
          : undefined

        setIsValidationError(false)
        if (!filteredCategories || filteredCategories.length === 0) {
          trackFormError({
            error: t('canNotAcceptPatientsUnder18'),
          })
        }
        setDisplayedCategories(filteredCategories)
        setRequiredConsent(isConsentRequiredVelthuis(dateOfBirth))
      }
    }
  }, [dateOfBirth, categories])

  return (
    <ScreenWrapper>
      <FormStyled method="post">
        <Header1>{t('header')}</Header1>
        <Stepper total={6} step={0} />
        <ScrollableContent>
          <Subheader>{t('fillDate')}</Subheader>

          <StyledFormElement>
            <Datepicker
              name="dateOfBirth"
              placeholder={tGeneral('dateOfBirth.placeholder')}
              label={tGeneral('dateOfBirth.label')}
              errorMessage={
                isValidationError ? tGeneral('dateOfBirth.error') : undefined
              }
              defaultValue={dateOfBirth}
              onChange={(e) => {
                const value = e.value?.toString()
                setPatientData({ dateOfBirth: value })
                trackFormInteraction({
                  action: 'click',
                  formFieldName: 'dateOfBirth',
                  formFieldValue: value,
                })
              }}
            />
            <InputCaption>
              <Trans
                i18nKey="selectCategory.learnMoreAboutDateOfBirth"
                components={[
                  <a
                    key="0"
                    href="https://www.zorginzicht.nl/kwaliteitsinstrumenten/cosmetische-zorg-kwaliteitskader"
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => {
                      trackFormInteraction({
                        action: 'click',
                        formFieldName: 'learnMoreAboutDateOfBirth',
                      })
                    }}
                  />,
                ]}
              />
            </InputCaption>
          </StyledFormElement>
          {dateOfBirth && !isValidationError && (
            <>
              {!displayedCategories ? (
                <BodyM>{t('canNotAcceptPatientsUnder18')}</BodyM>
              ) : (
                <>
                  <Subheader data-testid="category-title">
                    {t('subheader')}
                  </Subheader>
                  <FormElement>
                    <CardRadioGroup
                      name="category"
                      id="category"
                      value={selectedCategory}
                      onChange={(value: string) => {
                        setSelectedCategory(value)
                        setAppointmentType('')
                        trackFormInteraction({
                          action: 'click',
                          formFieldName: 'categories',
                          formFieldValue: categories?.find(
                            (category) => category.code === value,
                          )?.display,
                          formFieldValues: categories?.map(
                            (category) => category.display,
                          ),
                        })
                      }}
                    >
                      {displayedCategories.map((category) => (
                        <CardRadio
                          key={category.code}
                          value={category.code}
                          text={category.display}
                        />
                      ))}
                    </CardRadioGroup>
                  </FormElement>
                  {requiredConsent && (
                    <FormElement>
                      <Checkbox
                        id="consent"
                        label={t('parentalConsentCheckbox')}
                        checked={isConsentChecked}
                        onChange={(value: boolean) => {
                          setIsConsentChecked(!isConsentChecked)
                          trackFormInteraction({
                            action: 'click',
                            formFieldName: 'consent',
                            formFieldValue: `${value}`,
                          })
                        }}
                      />
                    </FormElement>
                  )}
                </>
              )}
            </>
          )}
        </ScrollableContent>
        <FormSubmit>
          <StyledActionButton
            variant="Primary"
            type="submit"
            disabled={
              !selectedCategory || (requiredConsent && !isConsentChecked)
            }
            onClick={() => {
              trackFormInteraction({
                action: 'next',
                formFieldName: 'submit',
              })
            }}
          >
            {t('actions.continue')}
          </StyledActionButton>
        </FormSubmit>
      </FormStyled>
    </ScreenWrapper>
  )
}
